// Triggers meet-analyze on a room and returns the analysis row { room_id,
// workspace_id, summary, suggestions, model, generated_at, applied_items? }
// or, when the room has no transcript yet, throws an Error with
// `err.status === 422` and `err.pending === true` so pages/meet.js
// onUpdate can render its existing isPending branch unchanged.
//
// migrated 2026-05-20 — direct Supabase edge-fn fetch replaced with
// `sdk.aiChat.meetAnalyze({ roomId, force })`. The endpoint lives on
// the main server now (Mongo-backed AI surface). Meet_rooms / meet_
// transcripts / meet_transcript_analyses still live in Supabase, but
// the server reads/writes them via the service-role key — UI doesn't
// have to wrangle Supabase auth headers anymore.
//
// 2026-06-02 — swapped the SDK accessor from `aiChat` to the unified
// `ai` namespace. sdk.ai.meetAnalyze is a passthrough that hits the
// same /ai-chat/meet-analyze server route; this keeps every meet/
// tickets/AppAssistant consumer pointed at `sdk.ai.*` for naming
// consistency, even though meet-analyze itself doesn't honor the
// provider-mode picker (the transcript analysis pipeline always
// runs on the server with managed keys).
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'

export const analyzeTranscript = async function (roomId, force = false) {
  const ai = await getSDK().getService('ai')
  let result
  try {
    result = await ai.meetAnalyze({ roomId, force })
  } catch (err) {
    const status = err?.status || 500
    const reason =
      err?.message ||
      (status === 422 ? 'no transcript available yet'
        : status === 403 ? 'not allowed to read this room'
          : status === 401 ? 'session expired — please sign in again'
            : `status ${status}`)
    const e = new Error(reason)
    e.status = status
    if (status === 422) e.pending = true
    throw e
  }
  // Unwrap the SDK's `{ data: row }` envelope — sdk.ai.meetAnalyze wraps
  // the analysis row, but every consumer (TranscriptPage Summary panel,
  // root.analysis readers) expects the BARE row. Without this a freshly
  // generated analysis landed as { data: {...} }, `analysis.summary` read
  // undefined, and the panel stayed on "No analysis yet." even though the
  // server wrote the row (found live 2026-06-12 — the summary appeared
  // only after a reload re-fetched the row through the unwrapping path).
  if (result && result.data !== undefined && result.summary === undefined) {
    result = result.data
  }
  // Server may return { pending: true, reason: 'no_transcript' } on a
  // fresh room with no transcript yet (200-status, matches legacy edge
  // fn shape). Convert to the Error+status:422 form the page expects.
  if (result?.pending) {
    const reason = result.reason === 'no_transcript' ? 'no transcript available yet' : (result.reason || 'analysis pending')
    const e = new Error(reason)
    e.status = 422
    e.pending = true
    throw e
  }
  return result
}
